import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { config } from '../../config';
import {
    syncTimerAction,
    setIsRunning,
    setTimeLeft,
    setTimerStatus,
    setStartTime,
    setElapsedOffset,
    setTotalTime,
    startTimer,
    setError
} from '../../redux/Slices/timerSlice';

// Icons
import Play from '../icons/Play';
import Pause from '../icons/Pause';
import Stop from '../icons/Stop';
import Timer from '../icons/Timer';
import Countdown from '../icons/Countdown';
import History from '../icons/History';

const TimerItem = ({ cardId, timer, onShowActivities }) => {
    const dispatch = useDispatch();
    const intervalRef = useRef(null);
    const [cardInfo, setCardInfo] = useState(null);

    // Local inputs for countdown
    const [hours, setHours] = useState(0);
    const [minutes, setMinutes] = useState(30);
    const [seconds, setSeconds] = useState(0);

    const {
        mode,
        timeLeft,
        isRunning,
        totalTime,
        timerStatus,
        error,
        title,
        startTime,
        elapsedOffset,
        stopwatchElapsed,
        countdownElapsed,
        hydrated,
    } = timer;

    // --- Sync countdown inputs on mount ---
    useEffect(() => {
        if (mode === 'countdown' && timeLeft > 0 && !isRunning) {
            setHours(Math.floor(timeLeft / 3600));
            setMinutes(Math.floor((timeLeft % 3600) / 60));
            setSeconds(timeLeft % 60);
        }
    }, [mode, timeLeft, isRunning]);

    // Timer update logic
    useEffect(() => {
        if (!isRunning || !startTime) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        const startTimeVal = startTime;
        const offset = elapsedOffset || 0;
        const duration = totalTime || 0;

        intervalRef.current = setInterval(() => {
            const now = Date.now();
            const sessionElapsed = Math.floor((now - startTimeVal) / 1000);

            if (mode === 'stopwatch') {
                const totalElapsed = offset + sessionElapsed;
                dispatch(setTimeLeft({ cardId, timeLeft: totalElapsed }));
            } else {
                const remaining = duration - (offset + sessionElapsed);
                if (remaining <= 0) {
                    clearInterval(intervalRef.current);
                    dispatch(setIsRunning({ cardId, isRunning: false }));
                    dispatch(setTimeLeft({ cardId, timeLeft: 0 }));
                    dispatch(syncTimerAction({ cardId, action: 'stop', mode, duration, elapsed: 0 }));
                } else {
                    dispatch(setTimeLeft({ cardId, timeLeft: remaining }));
                }
            }
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isRunning, startTime, elapsedOffset, totalTime, mode, cardId, dispatch]);

    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hrs > 0) {
            return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handlePause = async () => {
        dispatch(setIsRunning({ cardId, isRunning: false }));
        dispatch(setTimerStatus({ cardId, status: 'paused' }));
        clearInterval(intervalRef.current);

        try {
            await dispatch(syncTimerAction({
                cardId,
                action: 'pause',
                mode,
                duration: totalTime,
                elapsed: timeLeft
            })).unwrap();
        } catch (error) {
            console.error('Failed to pause timer:', error);
        }
    };

    const handleResume = async () => {
        if (isRunning) return;

        const duration = hours * 3600 + minutes * 60 + seconds;

        if (mode === 'countdown' && duration <= 0) {
            dispatch(setError({ cardId, error: 'Please set a valid duration for countdown' }));
            return;
        }

        dispatch(setIsRunning({ cardId, isRunning: true }));
        dispatch(setTimerStatus({ cardId, status: 'running' }));
        dispatch(setTotalTime({ cardId, totalTime: duration }));


        if (mode === 'countdown') {
            dispatch(setTimeLeft({ cardId, timeLeft: duration }));
        }

        try {

            const elapsed =
                mode === 'countdown'
                    ? countdownElapsed
                    : stopwatchElapsed;

            const actionResult = await dispatch(syncTimerAction({ cardId, action: 'resume', mode, duration, elapsed })).unwrap();

            const serverStartTime = new Date(actionResult.startTime).getTime();
            const serverElapsed =
                mode === 'countdown'
                    ? actionResult.countdownElapsed
                    : actionResult.stopwatchElapsed;

            if (Math.abs(serverStartTime - Date.now()) > 1000) {
                dispatch(setStartTime({ cardId, startTime: serverStartTime }));
                dispatch(setElapsedOffset({ cardId, elapsedOffset: serverElapsed }));
            } else {
                dispatch(setStartTime({ cardId, startTime: Date.now() }));
                dispatch(setElapsedOffset({ cardId, elapsedOffset: serverElapsed }));
            }
        } catch (err) {
            dispatch(setError({ cardId, error: err.message || 'Failed to resume timer' }));
        }
    };

    const handleStop = async () => {
        dispatch(setIsRunning({ cardId, isRunning: false }));
        dispatch(setTimerStatus({ cardId, status: 'stopped' }));
        dispatch(setTimeLeft({ cardId, timeLeft: 0 }));
        dispatch(setStartTime({ cardId, startTime: null }));
        dispatch(setElapsedOffset({ cardId, elapsedOffset: 0 }));
        dispatch(setTotalTime({ cardId, totalTime: 0 }));
        clearInterval(intervalRef.current);

        try {
            await dispatch(syncTimerAction({
                cardId,
                action: 'stop',
                mode,
                duration: totalTime,
                elapsed: 0
            })).unwrap();
        } catch (error) {
            console.error('Failed to stop timer:', error);
        }
    };


    if (!timer) {
        return (
            <div className="timer-item loading">
                <div className="timer-item-content">
                    <div className="timer-item-info">
                        <div className="card-title-skeleton"></div>
                        <div className="timer-display-skeleton"></div>
                    </div>
                </div>
            </div>
        );
    }



    return (
        <div className={`timer-item ${timerStatus}`} >
            <div className="timer-item-content">

                <Link to={`/c/${cardId}`} className="card-title">
                    {title || ''}
                </Link>

                <div className="timer-item-info">
                    <div className="timer-display">
                        <div className="timer-mode-icon">
                            {mode === 'countdown' ? (
                                <Countdown size={14} color="#10B981" />
                            ) : (
                                <Timer size={14} color="#10B981" />
                            )}
                        </div>
                        <span className="timer-time">{formatTime(timeLeft)}</span>
                    </div>

                    <div className="timer-item-controls">
                        <button
                            className="timer-activities-btn"
                            onClick={() => onShowActivities(cardId)}
                            data-tooltip-content="View Activities"
                            data-tooltip-position="top"
                        >
                            <History size={14} />
                        </button>

                        {isRunning ? (
                            <button
                                className="timer-pause-btn"
                                onClick={handlePause}
                                title="Pause"
                                data-tooltip-content="Pause"
                                data-tooltip-position="top"
                            >
                                <Pause size={14} color="#10B981" />
                            </button>
                        ) : (
                            <button
                                className="timer-play-btn"
                                onClick={handleResume}
                                data-tooltip-content="Resume"
                                data-tooltip-position="top"
                            >
                                <Play size={14} color="#F59E0B" />
                            </button>
                        )}

                        <button
                            className="timer-stop-btn"
                            onClick={handleStop}
                            data-tooltip-content="Stop"
                            data-tooltip-position="top"
                            disabled={timeLeft === 0}
                        >
                            <Stop size={14} color="#EF4444" />
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default TimerItem;
